import {Injectable} from "@angular/core";
import {HttpErrorResponse} from "@angular/common/http";
import {NgForm} from "@angular/forms";

import {Organization} from "./model/Organization";

import {OrganizationService} from "./services/OrganizationService";
import {LanguageDataService, Language} from "./services/LanguageDataService";
import {CountryDataService, Country} from "./services/CountryDataService";
import {LicenseTypeService, LicenseType} from "./services/LicenseTypeService";
import {AgbService} from "./services/AgbService";

type AppPage = "OrganizationNotFoundPage" | "LicenseAlreadyOrderedPage" | "StartPage"
  | "ContactAddressPage" | "BillingAddressPage" | "LicensePage" | "AgbPage" | "ReviewPage"
  | "SuccessPage" | "UserErrorPage" | "ServerErrorPage";

@Injectable({
  providedIn: "root"
})
export class AppController {

  private readonly _organization: Organization = new Organization();
  private readonly _organizationService: OrganizationService;
  private _availableLanguages: ReadonlyArray<Language> = [];
  private _availableCountries: ReadonlyArray<Country> = [];
  private _availableLicenseTypes: ReadonlyArray<LicenseType> = [];
  private _agbText: string = "loading...";
  private _currentPage: AppPage;
  private _licenseRequestSubmitted = false;
  private _latestErrorMessage: string;

  public constructor(organizationService: OrganizationService, languageDataService: LanguageDataService, countryDataService: CountryDataService, licenseTypeService: LicenseTypeService, agbService: AgbService) {
    this._organizationService = organizationService;
    const languagesPromise = languageDataService.getLanguages();
    const countriesPromise = countryDataService.getCountries();
    const licenseTypesPromise = licenseTypeService.loadLicenseTypes();
    const agbTextPromise = agbService.loadAgbText();
    const passedOrganizationId = (window.location.hash ? window.location.hash.substring(1) : null);
    const organizationPromise = (passedOrganizationId ? organizationService.loadOrganization(passedOrganizationId) : Promise.resolve(null));
    Promise.all([languagesPromise, countriesPromise, licenseTypesPromise, agbTextPromise, organizationPromise])
      .then(([languages, countries, licenseTypes, agbText, organization]) => {
        this._availableLanguages = languages;
        this._availableCountries = countries;
        this._availableLicenseTypes = licenseTypes;
        this._agbText = agbText;
        if (organization) {
          Object.assign(this._organization, organization);
          this._currentPage = (this._organization.newLicenseOrderDate ? "LicenseAlreadyOrderedPage" : "LicensePage");
        } else if (passedOrganizationId) {
          this._currentPage = "OrganizationNotFoundPage";
        } else {
          this._currentPage = "StartPage";
        }
        this._organization.selectInitialNewLicenseType(licenseTypes);
      })
      .catch(error => this.handleServiceCallFailure(error));
  }

  public get organization() {
    return this._organization;
  }

  public get availableLanguages() {
    return this._availableLanguages;
  }

  public get availableCountries() {
    return this._availableCountries;
  }

  public get availableLicenseTypes() {
    return this._availableLicenseTypes;
  }

  public get agbText() {
    return this._agbText;
  }

  public get licenseRequestSubmitted() {
    return this._licenseRequestSubmitted;
  }

  public get latestErrorMessage() {
    return this._latestErrorMessage;
  }

  public isVisible(page: AppPage): boolean {
    return page === this._currentPage;
  }

  public next(form?: NgForm): void {
    if (form && !form.valid) {
      Object.values(form.controls).forEach(c => c.markAsTouched()); // highlight invalid fields
      return;
    }
    switch (this._currentPage) {
      case "StartPage":
        this._currentPage = "ContactAddressPage";
        break;
      case "ContactAddressPage":
        if (this._organization.isSeparateBillingAddress) {
          this._organization.initBillingAddressIfNecessary();
          this._currentPage = "BillingAddressPage";
        } else {
          this._organization.copyContactAddressToBillingAddress();
          this._currentPage = "LicensePage";
        }
        break;
      case "BillingAddressPage":
        this._currentPage = "LicensePage";
        break;
      case "LicensePage":
        this._currentPage = "AgbPage";
        break;
      case "AgbPage":
        this._currentPage = "ReviewPage";
        break;
    }
  }

  public previous(): void {
    switch (this._currentPage) {
      case "ServerErrorPage":
      case "UserErrorPage":
        this._currentPage = "ReviewPage";
        break;
      case "ReviewPage":
        this._currentPage = "AgbPage";
        break;
      case "AgbPage":
        this._currentPage = "LicensePage";
        break;
      case "LicensePage":
        this._currentPage = (this._organization.isSeparateBillingAddress ? "BillingAddressPage" : "ContactAddressPage");
        break;
      case "BillingAddressPage":
        this._currentPage = "ContactAddressPage";
        break;
    }
  }

  public submitLicenseRequest(): void {
    if (!this._licenseRequestSubmitted) {
      this._licenseRequestSubmitted = true;
      this._organizationService.submitLicenseRequest(this.organization)
        .then(() => this._currentPage = "SuccessPage")
        .catch(error => {
          this.handleServiceCallFailure(error);
          this._licenseRequestSubmitted = false;
        });
    }
  }

  private handleServiceCallFailure(errorResponse: HttpErrorResponse): void {
    const errorInfo = errorResponse.error || {};
    console.error(`Server call failed: status = ${errorResponse.status} (${errorResponse.statusText}), error info = ${JSON.stringify(errorInfo)}`);
    this._latestErrorMessage = (errorInfo.message || errorInfo.name || `Die Verbindung zum Server ist fehlgeschlagen (HTTP Status ${errorResponse.status})`);
    this._currentPage = (errorInfo.isUserError ? "UserErrorPage" : "ServerErrorPage");
  }

}
